<template>
  <div class="tool-detail-page white-theme pollo-layout">
    <div class="container">
      <section class="first-screen">
      <div class="breadcrumb">
        <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
        <span>&rsaquo;</span>
        <router-link to="/tools">{{ $t('tools.page.allTools') }}</router-link>
        <span>&rsaquo;</span>
        <span>{{ toolText('name') }}</span>
      </div>

      <div class="first-screen-grid"><div class="action-column">
      <div v-if="!isLoggedIn" class="login-prompt">
        <div class="login-prompt-content">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <h2>{{ $t('tools.detail.loginRequired') }}</h2>
          <p>{{ $t('tools.detail.loginRequiredDesc') }}</p>
          <button class="btn btn-primary" @click="goLogin">{{ $t('tools.detail.loginNow') }}</button>
        </div>
      </div>

      <div v-else class="tool-interface" :class="{ 'tool-interface--formula': requiresSecondImage }">
        <div v-if="requiresSecondImage" class="upload-grid">
          <div class="upload-col">
            <span class="upload-col-label">{{ toolText('firstImageLabel') }}</span>
            <div
              class="tool-upload"
              @click="triggerUpload('first')"
              @dragover.prevent
              @drop.prevent="onDrop($event, 'first')"
            >
              <input type="file" ref="firstFileInput" accept="image/*" style="display:none" @change="handleFile($event, 'first')">
              <div v-if="!uploaded" class="upload-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                <p>{{ $t('tools.detail.clickUpload') }}</p>
                <span>JPG, PNG, WebP</span>
              </div>
              <div v-else class="upload-preview">
                <div class="preview-box preview-box--image">
                  <img v-if="previewSrc" :src="previewSrc" class="preview-img">
                </div>
                <button class="btn btn-secondary btn-sm" @click.stop="clearUpload('first')">{{ $t('tools.detail.reupload') }}</button>
              </div>
            </div>
          </div>

          <div class="upload-col">
            <span class="upload-col-label">{{ toolText('secondImageLabel') }}</span>
            <div
              class="tool-upload"
              @click="triggerUpload('second')"
              @dragover.prevent
              @drop.prevent="onDrop($event, 'second')"
            >
              <input type="file" ref="secondFileInput" accept="image/*" style="display:none" @change="handleFile($event, 'second')">
              <div v-if="!secondUploaded" class="upload-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><circle cx="12" cy="8" r="5"/><path d="M3 21v-2a7 7 0 0 1 7-7h4a7 7 0 0 1 7 7v2"/></svg>
                <p>{{ $t('tools.detail.clickUpload') }}</p>
                <span>JPG, PNG, WebP</span>
              </div>
              <div v-else class="upload-preview">
                <div class="preview-box preview-box--image">
                  <img v-if="secondPreviewSrc" :src="secondPreviewSrc" class="preview-img">
                </div>
                <p v-if="secondUploading" class="upload-status">{{ toolText('uploadingSecondImage') }}</p>
                <button class="btn btn-secondary btn-sm" :disabled="secondUploading" @click.stop="clearUpload('second')">{{ $t('tools.detail.reupload') }}</button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="tool-upload"
          @click="triggerUpload"
          @dragover.prevent
          @drop.prevent="onDrop"
        >
          <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="handleFile">
          <div v-if="!uploaded" class="upload-placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
            <p>{{ $t('tools.detail.clickUpload') }}{{ $t('tools.fileTypes.image') }}</p>
            <span>{{ $t('tools.detail.support') }} JPG, PNG, WebP</span>
          </div>
          <div v-else class="upload-preview">
            <div class="preview-box preview-box--image">
              <img v-if="previewSrc" :src="previewSrc" alt="Preview" class="preview-img">
            </div>
            <button class="btn btn-secondary" @click.stop="clearUpload">{{ $t('tools.detail.reupload') }}</button>
          </div>
        </div>

        <div class="tool-options">
          <button class="btn btn-primary btn-lg" :disabled="!canProcess || processing" @click="process">
            <svg v-if="processing" class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span v-else>{{ $t('tools.detail.process') }}</span>
          </button>
        </div>

        <div class="tool-result legacy-tool-result" v-if="processing || processed || (processError && !processing)">
          <div v-if="processing" class="gen-loading">
            <div class="loading-spinner"></div>
            <p>{{ $t('tools.detail.processing') }}</p>
          </div>
          <div v-else class="tool-output">
            <p v-if="processError && !resultSrc" class="tool-error tool-error--block">{{ processError }}</p>
            <div v-if="requiresSecondImage" class="output-comparison output-comparison-formula">
              <div class="output-formula-item">
                <span class="output-label">{{ toolText('firstImageLabel') }}</span>
                <div class="output-formula-image">
                  <img v-if="previewSrc" :src="previewSrc" :alt="toolText('firstImageLabel')">
                </div>
              </div>
              <div class="formula-operator">+</div>
              <div class="output-formula-item">
                <span class="output-label">{{ toolText('secondImageLabel') }}</span>
                <div class="output-formula-image">
                  <img v-if="secondPreviewSrc" :src="secondPreviewSrc" :alt="toolText('secondImageLabel')">
                </div>
              </div>
              <div class="formula-operator">=</div>
              <div class="output-formula-item">
                <span class="output-label">{{ $t('tools.detail.after') }}</span>
                <div class="output-formula-image">
                  <img v-if="resultSrc" :src="resultSrc" :alt="$t('tools.detail.after')">
                </div>
              </div>
            </div>
            <div v-else class="output-comparison">
              <div class="output-before">
                <span class="output-label">{{ $t('tools.detail.before') }}</span>
                <div class="output-image output-image--fill">
                  <img v-if="previewSrc" :src="previewSrc" alt="Before" class="compare-img">
                </div>
              </div>
              <div class="output-after">
                <span class="output-label">{{ $t('tools.detail.after') }}</span>
                <div class="output-image output-image--fill">
                  <img v-if="resultSrc" :src="resultSrc" alt="After" class="compare-img">
                </div>
              </div>
            </div>
            <div class="output-actions">
              <button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">⬇️ {{ $t('tools.detail.download') }}</button>
              <button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button>
            </div>
          </div>
        </div>
      </div>

      </div><div class="showcase-column">
    <div class="tool-hero"><div class="tool-hero-info"><h1>{{ toolText('name') }}</h1><p class="tool-hero-desc">{{ $t('brandSeo.toolDescription', { description: toolText('fullDesc') }) }}</p></div></div>
    <div v-if="!processing && !processed && toolConfig && toolConfig.beforeExtra" class="demo-section workspace-panel">
      <h2 class="demo-title">{{ $t('tools.detail.demo') }}</h2>
      <div class="demo-comparison demo-comparison-formula">
        <div class="demo-formula-item">
          <span class="demo-label">{{ toolText('firstImageLabel') }}</span>
          <div class="demo-image">
            <img :src="toolConfig.before()" :alt="toolText('firstImageLabel')">
          </div>
        </div>
        <div class="demo-operator">+</div>
        <div class="demo-formula-item">
          <span class="demo-label">{{ toolText('secondImageLabel') }}</span>
          <div class="demo-image">
            <img :src="toolConfig.beforeExtra()" :alt="toolText('secondImageLabel')">
          </div>
        </div>
        <div class="demo-operator">=</div>
        <div class="demo-formula-item">
          <span class="demo-label">{{ $t('tools.detail.after') }}</span>
          <div class="demo-image">
            <img :src="toolConfig.after()" :alt="$t('tools.detail.after')">
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!processing && !processed" class="demo-section workspace-panel">
        <h2 class="demo-title">{{ $t('tools.detail.demo') }}</h2>
        <div class="demo-comparison">
          <div class="demo-item">
            <span class="demo-label">{{ $t('tools.detail.before') }}</span>
            <div class="demo-image">
              <img :src="demoBeforeSrc" :alt="toolText('name') + ' before'">
            </div>
          </div>
          <div class="demo-arrow">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </div>
          <div class="demo-item">
            <span class="demo-label">{{ $t('tools.detail.after') }}</span>
            <div class="demo-image">
              <img :src="demoAfterSrc" :alt="toolText('name') + ' after'">
            </div>
          </div>
        </div>
      </div>

      <div class="processing-panel workspace-panel" v-else-if="processing"><div class="workspace-heading"><h2>{{ $t('tools.detail.processing') }}</h2></div><div class="processing-preview"><img v-if="previewSrc" :src="previewSrc" alt="Processing preview"><div class="processing-overlay"><div class="loading-spinner"></div></div></div><div class="processing-track"><span></span></div></div>
      <div class="result-panel workspace-panel" v-else><div class="workspace-heading"><h2>{{ $t('tools.detail.after') }}</h2></div><p v-if="processError && !resultSrc" class="tool-error tool-error--block">{{ processError }}</p><div v-if="resultSrc" class="output-comparison"><div class="output-before"><span class="output-label">{{ $t('tools.detail.before') }}</span><div class="output-image output-image--fill"><img v-if="previewSrc" :src="previewSrc" alt="Before" class="compare-img"></div></div><div class="output-after"><span class="output-label">{{ $t('tools.detail.after') }}</span><div class="output-image output-image--fill"><img :src="resultSrc" alt="After" class="compare-img"></div></div></div><div class="output-actions"><button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">{{ $t('tools.detail.download') }}</button><button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button></div></div>
      </div></div>
      </section>

      <div class="detail-section" style="margin-top:64px;">
        <h2>{{ $t('tools.detail.howToUse') }}</h2>
        <div class="steps">
          <div class="step" v-for="(step, idx) in steps" :key="idx">
            <div class="step-number">{{ idx + 1 }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h2>{{ $t('tools.detail.moreTools') }}</h2>
        <div class="grid-5">
          <ToolListCard
            v-for="t in otherTools"
            :key="t.id"
            :tool-id="t.id"
            :icon="t.icon"
            :badge="t.badge"
            :to="'/tools/' + t.id"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadFile, saveOrUpdateAIHistory, formatCreateDate, submitOldPhotoRepair, submitBlurryPhotoRepair, submitCartoonDigitalHuman, submitMangaDigitalHuman, submitOilPaintingDigitalHuman, submitIdPhotoGenerator, submitAiChangeClothes, submitAiTextRemover, submitAiLogoRemover, submitAiPassersbyRemover, pollOldPhotoRepairResult } from '@/api'

var MAX_FILE_BYTES = 15 * 1024 * 1024
var TOOL_CONFIGS = {
  PhotoRestoreDetail: {
    id: 'photo-restore',
    icon: '🖼️',
    before: function () { return require('@/assets/images/re_lod_photo.png') },
    after: function () { return require('@/assets/images/re_lod_photo2.png') },
    submit: submitOldPhotoRepair
  },
  BlurryPhotoRepairDetail: {
    id: 'blurry-photo-repair',
    icon: '🔍',
    before: function () { return require('@/assets/images/fix_blurry_images.png') },
    after: function () { return require('@/assets/images/fix_blurry_images2.jpg') },
    submit: submitBlurryPhotoRepair
  },
  CartoonDigitalHumanDetail: {
    id: 'cartoon-digital-human',
    icon: '🎨',
    before: function () { return require('@/assets/images/cartoons_anime.png') },
    after: function () { return require('@/assets/images/cartoons_anime2.jpg') },
    submit: submitCartoonDigitalHuman
  },
  MangaDigitalHumanDetail: {
    id: 'manga-digital-human',
    icon: '📚',
    before: function () { return require('@/assets/images/comic_digital_person.png') },
    after: function () { return require('@/assets/images/comic_digital_person2.jpg') },
    submit: submitMangaDigitalHuman
  },
  OilPaintingDigitalHumanDetail: {
    id: 'oil-painting-digital-human',
    icon: '🎨',
    before: function () { return require('@/assets/images/oil_painting_human.jpeg') },
    after: function () { return require('@/assets/images/oil_painting_human2.jpg') },
    submit: submitOilPaintingDigitalHuman
  },
  IdPhotoGeneratorDetail: {
    id: 'id-photo-generator',
    icon: '🪪',
    before: function () { return require('@/assets/images/photo_id_gen.png') },
    after: function () { return require('@/assets/images/photo_id_gen2.jpg') },
    submit: submitIdPhotoGenerator
  },
  AiChangeClothesDetail: {
    id: 'ai-change-clothes',
    icon: '👕',
    requiresSecondImage: true,
    before: function () { return require('@/assets/images/outfit_change.png') },
    beforeExtra: function () { return require('@/assets/images/outfit_change2.png') },
    after: function () { return require('@/assets/images/outfit_change3.png') },
    submit: function (imageUrl, image2Url) {
      if (!image2Url) return Promise.reject(new Error('image2Url is required'))
      return submitAiChangeClothes(imageUrl, image2Url)
    }
  },
  AiTextRemoverDetail: {
    id: 'ai-text-remover',
    icon: '🔤',
    before: function () { return require('@/assets/images/ai_remove_text.jpg') },
    after: function () { return require('@/assets/images/ai_remove_text2.jpg') },
    submit: submitAiTextRemover
  },
  AiLogoRemoverDetail: {
    id: 'ai-logo-remover',
    icon: '🏷️',
    before: function () { return require('@/assets/images/ai_remove_logo.png') },
    after: function () { return require('@/assets/images/ai_remove_logo2.jpg') },
    submit: submitAiLogoRemover
  },
  AiPassersbyRemoverDetail: {
    id: 'ai-passersby-remover',
    icon: '🚶',
    before: function () { return require('@/assets/images/remove_pedestrians.png') },
    after: function () { return require('@/assets/images/remove_pedestrians2.jpg') },
    submit: submitAiPassersbyRemover
  }
}

export default {
  name: 'PhotoRestoreDetail',
  data() {
    return {
      uploaded: false,
      secondUploaded: false,
      secondUploading: false,
      secondUploadedFile: null,
      secondPreviewSrc: '',
      secondRemoteUrl: '',
      processing: false,
      processed: false,
      previewSrc: '',
      resultSrc: '',
      uploadedFile: null,
      processError: null,
      poller: null
    }
  },
  beforeDestroy() {
    if (this.poller && typeof this.poller.stop === 'function') this.poller.stop()
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    toolConfig() {
      return TOOL_CONFIGS[this.$route.name] || TOOL_CONFIGS.PhotoRestoreDetail
    },
    toolId() {
      return this.toolConfig.id
    },
    toolIcon() {
      return this.toolConfig.icon
    },
    requiresSecondImage() {
      return !!(this.toolConfig && this.toolConfig.requiresSecondImage)
    },
    canProcess() {
      if (this.requiresSecondImage) {
        return this.uploaded && this.secondUploaded && !!this.secondRemoteUrl && !this.secondUploading
      }
      return this.uploaded
    },
    demoBeforeSrc() {
      return this.toolConfig.before()
    },
    demoAfterSrc() {
      return this.toolConfig.after()
    },
    otherTools() {
      var currentToolId = this.toolId
      return this.$store.state.tools.filter(function(t) { return t.id !== currentToolId }).slice(0, 5)
    },
    steps() {
      return [
        { title: this.$t('tools.steps.upload.title'), desc: this.$t('tools.steps.upload.desc') },
        { title: this.$t('tools.steps.process.title'), desc: this.$t('tools.steps.process.desc') },
        { title: this.$t('tools.steps.download.title'), desc: this.$t('tools.steps.download.desc') }
      ]
    }
  },
  methods: {
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },
    getToolText(id, field) {
      var tools = this.$t('tools')
      if (tools && typeof tools === 'object' && tools[id] && tools[id][field]) {
        return tools[id][field]
      }
      return this.$t('tools.' + id + '.' + field)
    },
    toolText(field) {
      return this.getToolText(this.toolId, field)
    },
    triggerUpload(type) {
      if (type === 'first' && this.$refs.firstFileInput) this.$refs.firstFileInput.click()
      else if (type === 'second' && this.$refs.secondFileInput) this.$refs.secondFileInput.click()
      else if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    onDrop(e, type) {
      var files = e.dataTransfer && e.dataTransfer.files
      if (files && files[0]) this.readFile(files[0], type)
    },
    handleFile(e, type) {
      var input = e.target
      var file = input.files && input.files[0]
      if (!file) {
        if (input) input.value = ''
        return
      }
      this.readFile(file, type)
      if (input) input.value = ''
    },
    readFile(file, type) {
      var self = this
      this.processError = null
      if (!file.type || file.type.indexOf('image/') !== 0) {
        this.processError = this.toolText('notImageFile')
        return
      }
      if (file.size > MAX_FILE_BYTES) {
        this.processError = this.toolText('fileTooLarge')
        return
      }
      var reader = new FileReader()
      reader.onload = function(ev) {
        var src = (ev.target && ev.target.result) ? String(ev.target.result) : ''
        if (type === 'second') {
          self.secondPreviewSrc = src
          self.secondUploadedFile = file
          self.secondUploaded = !!src
          self.uploadSecondImageRemote(file)
        } else if (type === 'first') {
          self.previewSrc = src
          self.uploadedFile = file
          self.uploaded = !!src
        } else {
          self.previewSrc = src
          self.uploadedFile = file
          self.uploaded = !!src
        }
        self.processed = false
        self.resultSrc = ''
      }
      reader.onerror = function() {
        self.processError = self.$t('common.generateFailedContactSupport')
      }
      reader.readAsDataURL(file)
    },
    uploadSecondImageRemote(file) {
      var self = this
      this.secondRemoteUrl = ''
      this.secondUploading = true
      uploadFile(file).then(function(uploadRes) {
        var imageUrl = typeof uploadRes === 'string' ? uploadRes : ''
        imageUrl = imageUrl || (uploadRes && (uploadRes.url || uploadRes.data || uploadRes.imageUrl))
        if (uploadRes && uploadRes.data && typeof uploadRes.data === 'object') {
          imageUrl = uploadRes.data.url || uploadRes.data.imageUrl || imageUrl
        }
        if (!imageUrl) return Promise.reject(new Error('image2Url is required'))
        self.secondRemoteUrl = imageUrl
        self.secondUploading = false
      }).catch(function(err) {
        self.secondUploaded = false
        self.secondPreviewSrc = ''
        self.secondUploadedFile = null
        self.secondUploading = false
        self.secondRemoteUrl = ''
        self.processError = err && err.message ? err.message : 'image2Url is required'
      })
    },
    clearUpload(type) {
      if (type === 'first') {
        this.uploaded = false
        this.previewSrc = ''
        this.uploadedFile = null
      } else if (type === 'second') {
        this.secondUploaded = false
        this.secondPreviewSrc = ''
        this.secondUploadedFile = null
        this.secondRemoteUrl = ''
        this.secondUploading = false
      } else {
        this.uploaded = false
        this.previewSrc = ''
        this.uploadedFile = null
        this.secondUploaded = false
        this.secondPreviewSrc = ''
        this.secondUploadedFile = null
        this.secondRemoteUrl = ''
        this.secondUploading = false
      }
      this.resultSrc = ''
      this.processed = false
      this.processing = false
      this.processError = null
      if (this.poller && typeof this.poller.stop === 'function') this.poller.stop()
      this.poller = null
    },
    reset() {
      this.clearUpload()
    },
    downloadResult() {
      if (!this.resultSrc) return
      var url = this.resultSrc
      var name = this.toolText('downloadName')
      if (url.indexOf('data:') === 0) {
        var a = document.createElement('a')
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        return
      }
      fetch(url)
        .then(function(r) { return r.blob() })
        .then(function(blob) {
          var u = URL.createObjectURL(blob)
          var a2 = document.createElement('a')
          a2.href = u
          a2.download = name
          document.body.appendChild(a2)
          a2.click()
          document.body.removeChild(a2)
          URL.revokeObjectURL(u)
        })
        .catch(function() {
          window.open(url, '_blank')
        })
    },
    process() {
      var self = this
      if (this.requiresSecondImage) {
        if (!this.previewSrc || !this.uploadedFile || !this.secondUploaded || !this.secondRemoteUrl || this.secondUploading) return
      } else if (!this.previewSrc || !this.uploadedFile) {
        return
      }
      this.processing = true
      this.processed = false
      this.processError = null

      var createDate = formatCreateDate(new Date())
      var toolId = this.toolId
      var aiName = this.getToolText(toolId, 'name')
      var aiDesc = this.getToolText(toolId, 'desc')
      var historyId = null
      var originalUrl = ''

      // 1. 先上传原图到服务器
      uploadFile(this.uploadedFile).then(function(url) {
        originalUrl = url
      }).catch(function() {
        self.processing = false
        self.processError = self.$t('tools.detail.uploadOriginalFailed')
        return Promise.reject('uploadOriginalFailed')
      }).then(function() {
        // 2. 创建历史记录，带上原图URL
        return saveOrUpdateAIHistory({
          aiName: aiName,
          taskId: '',
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        })
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
      }).then(function() {
        if (self.requiresSecondImage) {
          return self.toolConfig.submit(originalUrl, self.secondRemoteUrl)
        }
        return self.toolConfig.submit(originalUrl)
      }).then(function(submitRes) {
        var promptId = submitRes && submitRes.promptId ? submitRes.promptId : ''
        if (!promptId) return Promise.reject(new Error(toolId + ' did not return prompt_id'))
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: promptId,
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        }).catch(function() {})
        self.poller = pollOldPhotoRepairResult(promptId, function(err, result) {
          if (result && result.status === 'processing') return
          self.poller = null
          if (err) {
            self.processing = false
            self.processed = false
            self.resultSrc = ''
            self.processError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
            saveOrUpdateAIHistory({
              aiName: aiName,
              historyId: historyId,
              taskId: promptId,
              aiDesc: aiDesc,
              state: 3,
              message: (err && err.message) ? err.message : '',
              aiResultUrl: '',
              image_large_url: originalUrl,
              aiMediaType: 'image',
              createDate: createDate
            }).catch(function() {})
            return
          }
          var resultUrl = result && result.url ? result.url : ''
          self.processing = false
          self.processed = true
          self.resultSrc = resultUrl
          self.processError = null
          saveOrUpdateAIHistory({
            aiName: aiName,
            historyId: historyId,
            taskId: promptId,
            aiDesc: aiDesc,
            state: 2,
            aiResultUrl: resultUrl,
            image_large_url: originalUrl,
            aiMediaType: 'image',
            createDate: createDate
          }).catch(function() {})
        })
      }).catch(function(err) {
        if (err === 'uploadOriginalFailed') return
        self.processing = false
        self.processed = false
        self.resultSrc = ''
        self.processError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc,
          state: 3,
          message: (typeof err !== 'undefined' && err && err.message) ? err.message : '',
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        }).catch(function() {})
      })
    }
  }
}
</script>

<style scoped>
@import '../styles/toolPolloLayout.css';

@import './ModelDetailStyles.css';

.tool-detail-page { padding: 48px 0 80px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; margin-bottom: 32px; font-size: 13px; color: var(--text-muted); }
.breadcrumb a { color: var(--text-secondary); }
.breadcrumb a:hover { color: var(--accent); }
.tool-hero { margin-bottom: 32px; }
.tool-hero-info { text-align: center; max-width: 640px; margin: 0 auto; }
.tool-icon-lg { font-size: 48px; display: block; margin-bottom: 16px; }
.tool-hero-info h1 { font-size: 36px; font-weight: 800; margin-bottom: 12px; }
.tool-hero-desc { font-size: 16px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px; }
.demo-section { margin: 32px auto 0; padding: 24px; background: var(--bg-primary); border-radius: var(--radius-lg); max-width: 760px; }
.demo-title { font-size: 16px; font-weight: 600; text-align: center; margin-bottom: 20px; color: var(--text-primary); }
.demo-comparison { display: flex; align-items: center; justify-content: center; gap: 24px; flex-wrap: wrap; }
.demo-item { flex: 1; min-width: 200px; max-width: 300px; }
.demo-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; text-align: center; }
.demo-image { aspect-ratio: 3/4; border-radius: var(--radius-lg); overflow: hidden; background: var(--bg-secondary); }
.demo-image img { width: 100%; height: 100%; object-fit: cover; display: block; }
.demo-arrow { color: var(--text-muted); flex-shrink: 0; }
.login-prompt { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 24px 28px; text-align: center; margin: 32px auto 0; max-width: 680px; }
.login-prompt-content { max-width: 360px; margin: 0 auto; }
.login-prompt-content svg { display: block; margin: 0 auto; }
.login-prompt h2 { margin: 6px 0 4px; font-size: 20px; }
.login-prompt p { color: var(--text-muted); font-size: 14px; margin-bottom: 12px; }
.tool-interface { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 32px; margin: 32px auto 0; max-width: 680px; }
.tool-interface--formula { max-width: 760px; }
.upload-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.upload-col { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.upload-col-label { font-size: 13px; font-weight: 600; color: var(--text-secondary); }
.tool-upload { cursor: pointer; width: 100%; }
.upload-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px; border: 2px dashed var(--border-color); border-radius: var(--radius-lg); transition: border-color 0.2s; }
.upload-grid .upload-placeholder { padding: 48px 16px; }
.upload-placeholder:hover { border-color: var(--accent); }
.upload-placeholder p { margin-top: 12px; font-size: 14px; color: var(--text-secondary); }
.upload-grid .upload-placeholder p { font-size: 13px; }
.upload-placeholder span { font-size: 12px; color: var(--text-muted); }
.upload-preview { display: flex; flex-direction: column; align-items: center; gap: 16px; max-height: none; }
.upload-grid .upload-preview { gap: 12px; }
.upload-status { margin: 0; font-size: 12px; color: var(--text-muted); }
.preview-box { width: 360px; max-width: 100%; aspect-ratio: 4 / 3; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; overflow: hidden; background: #e8e8ec; }
.upload-grid .preview-box { width: 180px; height: 180px; aspect-ratio: auto; background: #e8e8ec; }
.preview-box--image { padding: 0; }
.preview-img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; display: block; }
.btn-sm { font-size: 12px; padding: 4px 12px; }
.tool-error { color: #f87171; font-size: 13px; margin: 0 0 12px; }
.tool-error--block { text-align: center; margin-bottom: 16px; }
.output-image--fill { padding: 0; overflow: hidden; background: var(--bg-primary); }
.compare-img { width: 100%; height: 100%; object-fit: contain; display: block; min-height: 200px; max-height: 420px; background: var(--bg-primary); }
.tool-options { display: flex; align-items: flex-end; gap: 16px; margin-top: 16px; justify-content: center; }
.tool-result { margin-top: 32px; }
.gen-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 12px; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid var(--border-color); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.output-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.output-comparison-formula {
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
}
.output-formula-item { min-width: 0; }
.output-formula-image {
  aspect-ratio: 3/4;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--bg-secondary);
}
.output-formula-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.formula-operator {
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}
.output-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; text-align: center; }
.output-image { aspect-ratio: 1; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; }
.output-actions { display: flex; gap: 12px; justify-content: center; margin-top: 12px; }
.detail-section { margin-bottom: 64px; }
.detail-section h2 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
.steps { display: flex; justify-content: center; gap: 48px; flex-wrap: wrap; }
.step { text-align: center; }
.step-number { width: 40px; height: 40px; border-radius: 50%; background: var(--accent); color: #000; display: flex; align-items: center; justify-content: center; font-weight: 700; margin: 0 auto 12px; }
.step h3 { margin-bottom: 6px; }
.step p { font-size: 13px; color: var(--text-muted); }
.spinner { animation: spin 1s linear infinite; }
@media (max-width: 768px) {
  .upload-grid { grid-template-columns: 1fr; }
  .steps { grid-template-columns: repeat(2, 1fr); }
  .output-comparison { grid-template-columns: 1fr; }
  .output-comparison-formula { grid-template-columns: 1fr; }
  .formula-operator { text-align: center; }
}
.demo-comparison-formula {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
}
.demo-formula-item {
  min-width: 0;
}
.demo-operator {
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

@media (max-width: 768px) {
  .demo-comparison-formula {
    grid-template-columns: 1fr;
  }

  .demo-operator {
    text-align: center;
  }
}
</style>
